import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  UserX, 
  UserCheck,
  Mail, 
  Phone, 
  Building, 
  MapPin,
  Briefcase,
  Package,
  Eye,
  Calendar,
  Hash,
  Plus,
  Search,
  Check,
  X,
  History,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  Download,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, formatDate, formatDateTime, getInitials, getStatusColor } from '@/lib/utils';
import { exportToCSV, exportToExcel, exportToPDF, ExportColumn } from '@/lib/export';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  isActive: boolean;
  hireDate?: string;
  createdAt: string;
  updatedAt: string;
  department?: { id: string; name: string };
  branch?: { id: string; name: string };
  assets?: Asset[];
}

interface Asset {
  id: string;
  assetId?: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  category?: { id: string; name: string };
  brand?: { id: string; name: string };
  model?: string;
}

interface AvailableAsset {
  id: string;
  assetId?: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  category?: { id: string; name: string };
  brand?: { id: string; name: string };
  model?: string;
}

interface AssetHistoryEntry {
  id: string;
  action: 'ASSIGNED' | 'UNASSIGNED' | 'TRANSFERRED';
  date: string;
  asset: {
    id: string;
    name: string;
    assetTag: string;
  };
  fromEmployee?: { id: string; firstName?: string; lastName?: string; employeeId?: string };
  toEmployee?: { id: string; firstName?: string; lastName?: string; employeeId?: string };
  notes?: string;
  performedBy?: { id: string; firstName?: string; lastName?: string; email?: string | null };
}

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showAssignAssetModal, setShowAssignAssetModal] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  // Fetch employee details
  const { data: employee, isLoading, error } = useQuery<Employee>({
    queryKey: ['employee', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}`);
      return response.data.data;
    },
  });

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/employees/${id}`, {
        isActive: !employee?.isActive,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setShowDeactivateConfirm(false);
      toast({
        title: 'Success',
        description: `Employee ${employee?.isActive ? 'deactivated' : 'activated'} successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.message || 'Failed to update employee status',
        variant: 'destructive',
      });
    },
  });

  // Fetch available assets (not assigned to any employee, must be assignable)
  const { data: availableAssets } = useQuery<AvailableAsset[]>({
    queryKey: ['available-assets-for-assignment'],
    queryFn: async () => {
      // Fetch all assets that are not assigned to any employee AND are assignable
      const response = await api.get('/assets?assigned=unassigned&isAssignable=true&limit=500');
      return response.data.data || [];
    },
    enabled: showAssignAssetModal,
  });

  // Fetch asset history (for auditing - all assets ever assigned to this employee)
  const { data: assetHistory, isLoading: historyLoading } = useQuery<AssetHistoryEntry[]>({
    queryKey: ['employee-assets-history', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}/assets/history`);
      return response.data.data || [];
    },
  });

  // Assign asset mutation
  const assignAssetMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const response = await api.post(`/assets/${assetId}/assign`, {
        employeeId: id,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['available-assets-for-assignment'] });
      setShowAssignAssetModal(false);
      setSelectedAssetId(null);
      setAssetSearch('');
      toast({
        title: 'Success',
        description: 'Asset assigned successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to assign asset',
        variant: 'destructive',
      });
    },
  });

  // Filter available assets based on search
  const filteredAssets = (availableAssets || []).filter((asset) => {
    if (!assetSearch) return true;
    const search = assetSearch.toLowerCase();
    return (
      asset.assetTag?.toLowerCase().includes(search) ||
      asset.assetId?.toLowerCase().includes(search) ||
      asset.name?.toLowerCase().includes(search) ||
      asset.serialNumber?.toLowerCase().includes(search) ||
      asset.category?.name?.toLowerCase().includes(search)
    );
  });

  // Export asset history
  const handleExportHistory = (format: 'csv' | 'excel' | 'pdf') => {
    if (!assetHistory || assetHistory.length === 0 || !employee) return;

    const columns: ExportColumn[] = [
      { header: 'Date', accessor: (row) => formatDateTime(row.date), width: 20 },
      { header: 'Action', accessor: 'action', width: 12 },
      { header: 'Asset Tag', accessor: 'asset.assetTag', width: 15 },
      { header: 'Asset Name', accessor: 'asset.name', width: 25 },
      { header: 'Assigned To', accessor: (row) => row.toEmployee ? `${row.toEmployee.firstName || ''} ${row.toEmployee.lastName || ''}`.trim() : '-', width: 20 },
      { header: 'Unassigned From', accessor: (row) => row.fromEmployee ? `${row.fromEmployee.firstName || ''} ${row.fromEmployee.lastName || ''}`.trim() : '-', width: 20 },
      { header: 'Performed By', accessor: (row) => row.performedBy ? `${row.performedBy.firstName || ''} ${row.performedBy.lastName || ''}`.trim() : '-', width: 20 },
      { header: 'Notes', accessor: 'notes', width: 30 },
    ];

    const options = {
      filename: `${employee.employeeId}-asset-history-${new Date().toISOString().split('T')[0]}`,
      title: `Asset History - ${employee.firstName} ${employee.lastName}`,
      subtitle: `Employee ID: ${employee.employeeId} | Generated on ${new Date().toLocaleString()}`,
      columns,
      data: assetHistory,
    };

    switch (format) {
      case 'csv':
        exportToCSV(options);
        break;
      case 'excel':
        exportToExcel(options);
        break;
      case 'pdf':
        exportToPDF(options);
        break;
    }

    toast({
      title: 'Export Complete',
      description: `Asset history exported to ${format.toUpperCase()} successfully`,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found</p>
        <Button variant="link" onClick={() => navigate('/employees')} className="mt-2">
          Back to Employees
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/employees">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xl font-bold text-primary">
                {getInitials(employee.firstName, employee.lastName)}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold">{employee.firstName} {employee.lastName}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-sm text-muted-foreground">{employee.employeeId}</span>
                <span className={cn(
                  "px-2 py-0.5 text-xs font-medium rounded-full",
                  employee.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                )}>
                  {employee.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-12 sm:ml-0">
          <Button variant="outline" asChild>
            <Link to={`/employees/${id}/edit`}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Link>
          </Button>
          <Button 
            variant={employee.isActive ? "destructive" : "default"}
            onClick={() => setShowDeactivateConfirm(true)}
          >
            {employee.isActive ? (
              <>
                <UserX className="w-4 h-4 mr-2" />
                Deactivate
              </>
            ) : (
              <>
                <UserCheck className="w-4 h-4 mr-2" />
                Activate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Deactivate Confirmation Modal */}
      {showDeactivateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">
                {employee.isActive ? 'Deactivate Employee?' : 'Activate Employee?'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {employee.isActive 
                  ? 'Deactivating this employee will release all assigned assets and make them available for reassignment.'
                  : 'Activating this employee will allow them to be assigned assets again.'}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeactivateConfirm(false)}>
                  Cancel
                </Button>
                <Button 
                  variant={employee.isActive ? "destructive" : "default"}
                  onClick={() => toggleActiveMutation.mutate()}
                  disabled={toggleActiveMutation.isPending}
                >
                  {toggleActiveMutation.isPending 
                    ? 'Processing...' 
                    : employee.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee Information */}
          <Card>
            <CardHeader>
              <CardTitle>Employee Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{employee.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{employee.phone || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Job Title</p>
                    <p className="font-medium">{employee.jobTitle || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="font-medium">{employee.department?.name || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Branch</p>
                    <p className="font-medium">{employee.branch?.name || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Hire Date</p>
                    <p className="font-medium">{employee.hireDate ? formatDate(employee.hireDate) : '-'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assigned Assets */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Assigned Assets
              </CardTitle>
              <div className="flex items-center gap-2">
                {employee.isActive && (
                  <Button 
                    variant="default" 
                    size="sm"
                    onClick={() => setShowAssignAssetModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Assign Asset
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/employees/${id}/assets`}>
                    View All Assets
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!employee.assets || employee.assets.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No assets assigned to this employee</p>
                  {employee.isActive && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setShowAssignAssetModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Assign First Asset
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Asset Tag</th>
                        <th className="text-left p-3 font-medium">Name</th>
                        <th className="text-left p-3 font-medium">Category</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-right p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {employee.assets.map((asset) => (
                        <tr key={asset.id} className="hover:bg-muted/50">
                          <td className="p-3">
                            <span className="font-mono text-sm font-medium text-primary">
                              {asset.assetTag}
                            </span>
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{asset.name}</p>
                              {asset.serialNumber && (
                                <p className="text-sm text-muted-foreground font-mono">
                                  S/N: {asset.serialNumber}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            {asset.category?.name || '-'}
                          </td>
                          <td className="p-3">
                            <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getStatusColor(asset.status))}>
                              {asset.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/assets/${asset.id}`}>
                                <Eye className="w-4 h-4" />
                              </Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Asset History (for auditing) */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Asset History
              </CardTitle>
              <div className="flex items-center gap-2">
                {assetHistory && assetHistory.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExportHistory('csv')}>
                        <FileText className="w-4 h-4 mr-2" />
                        Export as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportHistory('excel')}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Export as Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportHistory('pdf')}>
                        <FileText className="w-4 h-4 mr-2" />
                        Export as PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/employees/${id}/assets`}>
                    View Full History
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full spinner" />
                </div>
              ) : !assetHistory || assetHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No asset history available</p>
                </div>
              ) : (
                <div className="space-y-0">
                  {/* Timeline - show last 5 entries */}
                  <div className="relative">
                    {assetHistory.slice(0, 5).map((entry, index) => (
                      <div key={entry.id} className="relative flex gap-4 pb-4">
                        {/* Timeline line */}
                        {index < Math.min(assetHistory.length, 5) - 1 && (
                          <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-border" />
                        )}
                        {/* Timeline dot */}
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10",
                          entry.action === 'ASSIGNED' ? 'bg-green-100' :
                          entry.action === 'UNASSIGNED' ? 'bg-red-100' :
                          'bg-blue-100'
                        )}>
                          {entry.action === 'ASSIGNED' && <UserPlus className="w-4 h-4 text-green-600" />}
                          {entry.action === 'UNASSIGNED' && <UserMinus className="w-4 h-4 text-red-600" />}
                          {entry.action === 'TRANSFERRED' && <ArrowRightLeft className="w-4 h-4 text-blue-600" />}
                        </div>
                        {/* Content */}
                        <div className="flex-1 bg-muted/30 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={cn(
                                  "px-2 py-0.5 text-xs font-medium rounded-full",
                                  entry.action === 'ASSIGNED' ? 'bg-green-100 text-green-800' :
                                  entry.action === 'UNASSIGNED' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                )}>
                                  {entry.action}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDateTime(entry.date)}
                                </span>
                              </div>
                              <p className="text-sm font-medium">
                                <Link to={`/assets/${entry.asset.id}`} className="hover:underline text-primary">
                                  {entry.asset.assetTag}
                                </Link>
                                {' - '}{entry.asset.name}
                              </p>
                              {/* Show assignment details */}
                              {entry.action === 'ASSIGNED' && entry.toEmployee && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Assigned to: {entry.toEmployee.firstName} {entry.toEmployee.lastName}
                                  {entry.toEmployee.employeeId && ` (${entry.toEmployee.employeeId})`}
                                </p>
                              )}
                              {entry.action === 'UNASSIGNED' && entry.fromEmployee && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Unassigned from: {entry.fromEmployee.firstName} {entry.fromEmployee.lastName}
                                  {entry.fromEmployee.employeeId && ` (${entry.fromEmployee.employeeId})`}
                                </p>
                              )}
                              {entry.action === 'TRANSFERRED' && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {entry.fromEmployee && `From: ${entry.fromEmployee.firstName} ${entry.fromEmployee.lastName}`}
                                  {entry.fromEmployee && entry.toEmployee && ' → '}
                                  {entry.toEmployee && `To: ${entry.toEmployee.firstName} ${entry.toEmployee.lastName}`}
                                </p>
                              )}
                              {entry.performedBy && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  By: {entry.performedBy.firstName} {entry.performedBy.lastName}
                                  {entry.performedBy.email && ` (${entry.performedBy.email})`}
                                </p>
                              )}
                              {entry.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">{entry.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {assetHistory.length > 5 && (
                    <div className="text-center pt-2">
                      <Button variant="link" size="sm" asChild>
                        <Link to={`/employees/${id}/assets`}>
                          View all {assetHistory.length} history entries
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Assets</span>
                <span className="font-semibold">{employee.assets?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">History Entries</span>
                <span className="font-semibold">{assetHistory?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className={cn(
                  "px-2 py-0.5 text-xs font-medium rounded-full",
                  employee.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                )}>
                  {employee.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Record Details */}
          <Card>
            <CardHeader>
              <CardTitle>Record Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Hash className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Employee ID</p>
                  <p className="font-mono text-sm">{employee.employeeId}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="text-sm">{formatDate(employee.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="text-sm">{formatDate(employee.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to={`/employees/${id}/edit`}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Employee
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to={`/employees/${id}/assets`}>
                  <Package className="w-4 h-4 mr-2" />
                  View Assets
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assign Asset Modal */}
      {showAssignAssetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <CardTitle>Assign Asset to {employee.firstName} {employee.lastName}</CardTitle>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => {
                  setShowAssignAssetModal(false);
                  setSelectedAssetId(null);
                  setAssetSearch('');
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-4">
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by asset tag, name, serial number..."
                  className="pl-10"
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                />
              </div>

              {/* Asset List */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  {filteredAssets.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        {assetSearch ? 'No assets found matching your search' : 'No available assets in stock'}
                      </p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="w-10 p-3"></th>
                          <th className="text-left p-3 font-medium">Asset Tag</th>
                          <th className="text-left p-3 font-medium">Asset ID / Name</th>
                          <th className="text-left p-3 font-medium">Category</th>
                          <th className="text-left p-3 font-medium">Brand/Model</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredAssets.map((asset) => (
                          <tr 
                            key={asset.id}
                            className={cn(
                              "hover:bg-muted/50 cursor-pointer transition-colors",
                              selectedAssetId === asset.id && "bg-primary/10"
                            )}
                            onClick={() => setSelectedAssetId(asset.id)}
                          >
                            <td className="p-3 text-center">
                              <div className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                selectedAssetId === asset.id 
                                  ? "border-primary bg-primary" 
                                  : "border-input"
                              )}>
                                {selectedAssetId === asset.id && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="font-mono text-sm font-medium text-primary">
                                {asset.assetTag}
                              </span>
                            </td>
                            <td className="p-3">
                              <div>
                                {asset.name && (
                                  <p className="font-medium">{asset.name}</p>
                                )}
                                {asset.assetId && (
                                  <p className={cn("font-mono text-sm", asset.name ? "text-muted-foreground" : "font-medium")}>
                                    {asset.assetId}
                                  </p>
                                )}
                                {asset.serialNumber && (
                                  <p className="text-xs text-muted-foreground">S/N: {asset.serialNumber}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-sm">
                              {asset.category?.name || '-'}
                            </td>
                            <td className="p-3 text-sm">
                              <div>
                                {asset.brand?.name && <p>{asset.brand.name}</p>}
                                {asset.model && <p className="text-muted-foreground text-xs">{asset.model}</p>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Selected Asset Info */}
              {selectedAssetId && (
                <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm font-medium">
                    Selected: {filteredAssets.find(a => a.id === selectedAssetId)?.assetTag} - {filteredAssets.find(a => a.id === selectedAssetId)?.name || filteredAssets.find(a => a.id === selectedAssetId)?.assetId}
                  </p>
                </div>
              )}
            </CardContent>
            <div className="border-t p-4 flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAssignAssetModal(false);
                  setSelectedAssetId(null);
                  setAssetSearch('');
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!selectedAssetId || assignAssetMutation.isPending}
                onClick={() => selectedAssetId && assignAssetMutation.mutate(selectedAssetId)}
              >
                {assignAssetMutation.isPending ? 'Assigning...' : 'Assign Asset'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
